import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  STAGE_LABELS, money, shortDate, daysUntil, programName, contactName, CHANNEL_LABELS
} from './constants';
import './Dashboard.css';

// The season engine. The question this page answers every morning is
// "who's coming up and who's slipping" -- not "what stage is this deal in".

const SeasonRow = ({ season, dateField, dateLabel, tone }) => {
  const navigate = useNavigate();
  const days = daysUntil(season[dateField]);
  const person = contactName(season);
  const phone = season.contact_mobile || season.contact_phone;

  const dayText = days === null ? '' :
    days === 0 ? 'today' :
    days > 0 ? `in ${days}d` :
    `${Math.abs(days)}d ago`;

  return (
    <li className={`season-row tone-${tone}`}>
      <button
        type="button"
        className="season-main"
        onClick={() => navigate(`/crm/organizations/${season.organization_id}`)}
      >
        <span className="season-org">{season.organization_name}</span>
        <span className="season-program">{programName(season)} · {season.year}</span>
        <span className="season-meta">
          <span className={`stage-pill stage-${season.stage}`}>{STAGE_LABELS[season.stage]}</span>
          <span className="season-date">{dateLabel} {shortDate(season[dateField])} {dayText && <em>{dayText}</em>}</span>
          {season.estimated_value ? <span className="season-value">{money(season.estimated_value)} est.</span> : null}
        </span>
      </button>

      {person && (
        <div className="season-contact">
          <span className="contact-name">
            {person}
            {season.contact_preferred_channel && season.contact_preferred_channel !== 'unknown' && (
              <em> · prefers {CHANNEL_LABELS[season.contact_preferred_channel].toLowerCase()}</em>
            )}
          </span>
          <span className="contact-actions">
            {phone && <a className="tap-btn" href={`tel:${phone}`}>Call</a>}
            {phone && <a className="tap-btn" href={`sms:${phone}`}>Text</a>}
            {season.contact_email && <a className="tap-btn" href={`mailto:${season.contact_email}`}>Email</a>}
          </span>
        </div>
      )}
    </li>
  );
};

const SeasonList = ({ title, blurb, rows, dateField, dateLabel, tone, emptyText }) => (
  <section className={`season-list tone-${tone}`}>
    <header>
      <h2>{title} <span className="count">{rows.length}</span></h2>
      <p>{blurb}</p>
    </header>
    {rows.length === 0
      ? <p className="empty">{emptyText}</p>
      : <ul>{rows.map(s => (
          <SeasonRow key={s.id} season={s} dateField={dateField} dateLabel={dateLabel} tone={tone} />
        ))}</ul>}
  </section>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/crm/dashboard');
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the CRM dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading && !data) return <div className="crm-dashboard"><p className="loading">Loading…</p></div>;
  if (error) return <div className="crm-dashboard"><p className="error-banner">{error}</p></div>;
  if (!data) return null;

  const s = data.summary || {};

  return (
    <div className="crm-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Team Sales CRM</h1>
          <p className="subtitle">Who's coming up and who's slipping</p>
        </div>
        <span className="season-context">Now selling the {data.year} season</span>
      </div>

      <div className="stats-cards">
        <div className="stat-card stat-committed">
          <div className="stat-value">{money(s.committed_value)}</div>
          <div className="stat-label">Committed · {s.won_count || 0} ordered</div>
        </div>
        <div className="stat-card stat-expected">
          <div className="stat-value">{money(s.expected_value)}</div>
          <div className="stat-label">Expected · {s.open_count || 0} still open</div>
        </div>
        <div className="stat-card stat-margin">
          <div className="stat-value">{money(s.committed_margin)}</div>
          <div className="stat-label">Gross margin on committed</div>
        </div>
        <div className="stat-card stat-risk">
          <div className="stat-value">{s.slipping_count || 0}</div>
          <div className="stat-label">Past window, nothing ordered</div>
        </div>
      </div>

      <div className="money-strip">
        <span><strong>{money(s.collected)}</strong> collected</span>
        <span><strong>{money(s.outstanding)}</strong> outstanding</span>
        <span><strong>{money(s.lost_value)}</strong> lost ({s.lost_count || 0})</span>
      </div>

      <div className="season-lists">
        <SeasonList
          title="Due now"
          blurb="Follow-ups you set that have come due"
          rows={data.dueActions || []}
          dateField="next_action_at"
          dateLabel="Due"
          tone="due"
          emptyText="Nothing due. Set follow-ups as you log activity."
        />
        <SeasonList
          title="Slipping"
          blurb="Past their buying window with nothing ordered — that's a customer who quietly went elsewhere"
          rows={data.slipping || []}
          dateField="window_end"
          dateLabel="Window closed"
          tone="risk"
          emptyText="Nothing has slipped past its window."
        />
        <SeasonList
          title="Open, nobody has called"
          blurb="The window is open right now and there's no contact logged"
          rows={data.openNoContact || []}
          dateField="window_end"
          dateLabel="Closes"
          tone="warn"
          emptyText="Every open window has been worked."
        />
        <SeasonList
          title={`Opening in the next ${data.days} days`}
          blurb="Get ahead of these before the window opens"
          rows={data.openingSoon || []}
          dateField="window_start"
          dateLabel="Opens"
          tone="soon"
          emptyText="No windows opening in this stretch."
        />
      </div>

      {(data.bySport || []).length > 0 && (
        <section className="by-sport">
          <h2>Revenue by sport · {data.year}</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Sport</th><th>Seasons</th><th>Revenue</th><th>Cost</th><th>Margin</th><th>Margin %</th>
                </tr>
              </thead>
              <tbody>
                {data.bySport.map(row => {
                  const pct = Number(row.revenue) > 0
                    ? `${Math.round((Number(row.margin) / Number(row.revenue)) * 100)}%`
                    : '—';
                  return (
                    <tr key={row.sport}>
                      <td>{row.sport}</td>
                      <td>{row.seasons}</td>
                      <td>{money(row.revenue)}</td>
                      <td>{money(row.cost)}</td>
                      <td>{money(row.margin)}</td>
                      <td>{pct}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
